import { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/use-auth'
import { useLang } from '@/hooks/use-lang'
import { t } from '@/lib/i18n'
import { api } from '@/lib/api'
import { workspaceUrl } from '@/lib/workspace'
import { Wordmark } from '@/components/wordmark'

export function LoginPage() {
  useLang()
  const { login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  // ProtectedRoute stores the originally-requested URL here when it bounces an
  // unauthenticated user to /login; default to the app root otherwise.
  const from = (location.state as { from?: string } | null)?.from ?? '/'
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const redirectUrl = await login(email, password)
      // Invite acceptance takes precedence: an existing user who followed an
      // invite link (e.g. bounced here from /signup) joins the workspace and
      // lands on its subdomain. Falls through to the normal redirect if the
      // invite is invalid/expired.
      const inviteCode = new URLSearchParams(location.search).get('invite')
      if (inviteCode) {
        try {
          const joined = await api.post<{ slug: string }>('/api/invites/accept', { invite_code: inviteCode })
          window.location.href = workspaceUrl(joined.slug)
          return
        } catch {
          // fall through to the normal post-login redirect
        }
      }
      if (redirectUrl) {
        // Cloud mode bounces through {slug}.<domain>/auth/callback to mint the
        // cross-subdomain session. Preserve the originally-requested deep link
        // (e.g. the /oauth/login?login_challenge=… an OAuth client sent the user
        // to) so auth-callback returns there instead of the dashboard root.
        const url = new URL(redirectUrl)
        if (from && from !== '/') url.searchParams.set('return_to', from)
        window.location.href = url.toString()
      } else {
        navigate(from, { replace: true })
      }
    } catch {
      setError(t('invalidCredentials'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg)' }}>
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <img src="/logo.png" className="w-12 h-12 mx-auto mb-4" alt="WebhookLens" />
          <h1 className="text-xl font-semibold" style={{ color: 'var(--white)' }}><Wordmark /></h1>
          <p className="text-sm mt-1" style={{ color: 'var(--muted)' }}>{t('signInTitle')}</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4 p-6 rounded-xl" style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border)' }}>
          <a
            href="/api/auth/github"
            className="flex items-center justify-center gap-2.5 w-full py-2.5 rounded-lg text-sm font-medium transition"
            style={{ backgroundColor: '#21262d', color: '#e6edf3', border: '1px solid #30363d' }}
            onMouseEnter={e => (e.currentTarget as HTMLElement).style.backgroundColor = '#30363d'}
            onMouseLeave={e => (e.currentTarget as HTMLElement).style.backgroundColor = '#21262d'}
          >
            <svg className="w-5 h-5" viewBox="0 0 98 96" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" clipRule="evenodd" d="M48.854 0C21.839 0 0 22 0 49.217c0 21.756 13.993 40.172 33.405 46.69 2.427.49 3.316-1.059 3.316-2.362 0-1.141-.08-5.052-.08-9.127-13.59 2.934-16.42-5.867-16.42-5.867-2.184-5.704-5.42-7.17-5.42-7.17-4.448-3.015.324-3.015.324-3.015 4.934.326 7.523 5.052 7.523 5.052 4.367 7.496 11.404 5.378 14.235 4.074.404-3.178 1.699-5.378 3.074-6.6-10.839-1.141-22.243-5.378-22.243-24.283 0-5.378 1.94-9.778 5.014-13.2-.485-1.222-2.184-6.275.486-13.038 0 0 4.125-1.304 13.426 5.052a46.97 46.97 0 0 1 12.214-1.63c4.125 0 8.33.571 12.213 1.63 9.302-6.356 13.427-5.052 13.427-5.052 2.67 6.763.97 11.816.485 13.038 3.155 3.422 5.015 7.822 5.015 13.2 0 18.905-11.404 23.06-22.324 24.283 1.78 1.548 3.316 4.481 3.316 9.126 0 6.6-.08 11.897-.08 13.526 0 1.304.89 2.853 3.316 2.364 19.412-6.52 33.405-24.935 33.405-46.691C97.707 22 75.788 0 48.854 0z" />
            </svg>
            {t('sign_in_with_github')}
          </a>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px" style={{ backgroundColor: 'var(--border)' }} />
            <span className="text-xs" style={{ color: 'var(--muted)' }}>{t('or')}</span>
            <div className="flex-1 h-px" style={{ backgroundColor: 'var(--border)' }} />
          </div>
          {error && (
            <p className="text-sm p-3 rounded-lg" style={{ color: 'var(--red)', backgroundColor: 'color-mix(in srgb, var(--red) 12%, transparent)', border: '1px solid color-mix(in srgb, var(--red) 25%, transparent)' }}>
              {error}
            </p>
          )}
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--muted)' }}>{t('email')}</label>
            <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none transition"
              style={{ backgroundColor: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }}
              onFocus={e => (e.target as HTMLInputElement).style.borderColor = 'var(--accent)'}
              onBlur={e => (e.target as HTMLInputElement).style.borderColor = 'var(--border)'}
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--muted)' }}>{t('password')}</label>
            <input type="password" required value={password} onChange={e => setPassword(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none transition"
              style={{ backgroundColor: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }}
              onFocus={e => (e.target as HTMLInputElement).style.borderColor = 'var(--accent)'}
              onBlur={e => (e.target as HTMLInputElement).style.borderColor = 'var(--border)'}
            />
          </div>
          <button type="submit" disabled={loading}
            className="btn-accent w-full py-2.5 rounded-lg text-sm font-medium"
          >
            {loading ? t('signingIn') : t('signIn')}
          </button>
          <p className="text-sm text-center" style={{ color: 'var(--muted)' }}>
            <Link to="/forgot-password" className="font-medium transition" style={{ color: 'var(--accent)' }}
              onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = 'var(--accent-bright)'}
              onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = 'var(--accent)'}
            >
              {t('forgotPassword')}
            </Link>
          </p>
          <p className="text-sm text-center" style={{ color: 'var(--muted)' }}>
            {t('noAccount')}{' '}
            <Link to="/signup" className="font-medium transition" style={{ color: 'var(--accent)' }}
              onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = 'var(--accent-bright)'}
              onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = 'var(--accent)'}
            >
              {t('signUp')}
            </Link>
          </p>
        </form>
      </div>
    </div>
  )
}
